import { api, download, listPages, paged } from '../console/api.ts';
import { type CarrierView, carrierCell, carrierName } from '../console/carriers.ts';
import {
  empty,
  field,
  loadMore,
  pageHead,
  pill,
  reportButtons,
  skel,
} from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import {
  agentName,
  date,
  esc,
  FREQUENCY,
  fmt,
  guatemalaMonth,
  icon,
  iso,
  type Money,
  PRODUCT,
  when,
} from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { canQuote, followsRenewals, has } from '../console/session.ts';
import { $, act, toast } from '../console/ui.ts';
import { dateControl, monthControl, selectControl, textControl } from '../controls.ts';
import { cobranzaClicks } from './cobranza.ts';
import type { Option } from './cotizar.ts';
import { type CancellationRequestView } from './poliza-acciones.ts';
import {
  collectionPill,
  DOCUMENT,
  daysText,
  isPolicyTab,
  lineAndPlan,
  loadPolicyPage,
  POLICY_STATUS,
  type PolicySummary,
  type PolicyTab,
  policyMissing,
  policyPageHtml,
  selectTab,
} from './poliza-detalle.ts';

/**
 * «Pólizas y recibos» (#310): the agency's policies in a table — policy (number, ramo · plan),
 * client (name and DPI), carrier, agent, annual premium, how it is paid, when it ends, its status
 * and its collection — with chips that count vigentes, por renovar, en mora and canceladas, the
 * annual premium of what is filtered, the search and filters (kept in the URL) and the Excel of
 * the list. Each row opens the policy's own page (poliza-detalle.ts), whose buttons this view
 * answers: collect, send a payment link, renew, endorse or cancel it.
 */

/**
 * How a policy's receipts stand, in the «Cobranza» filter (the API's states, the same words as
 * the customer's page): label in the filter, label in the row, pill.
 */
export const POLICY_STATE: Record<string, [filter: string, row: string, pill: string]> = {
  CURRENT: ['Al día', 'Al día', 'ok'],
  OVERDUE: ['Recibo vencido', 'Recibo vencido', 'warn'],
  DELINQUENT: ['En mora', 'En mora', 'bad'],
  UNCONFIRMED: ['Sin confirmar con la aseguradora', 'Sin confirmar', 'warn'],
  PAID: ['Pagada', 'Pagada', 'ok'],
  CANCELLED: ['Cancelada', 'Cancelada', 'CANCELLED'],
};

/** The chips over the list (#310): the API's segments, with their counts. */
export const POLICY_CHIPS: [segment: string, label: string, count: keyof SummaryView['counts']][] =
  [
    ['', 'Todas', 'all'],
    ['IN_FORCE', 'Vigentes', 'inForce'],
    ['RENEWAL_DUE', 'Por renovar', 'renewalDue'],
    ['DELINQUENT', 'En mora', 'delinquent'],
    ['CANCELLED', 'Canceladas', 'cancelled'],
  ];

/** GET /v1/policies/summary: the chips' counts and the annual premium of what is shown. */
type SummaryView = {
  counts: {
    all: number;
    inForce: number;
    renewalDue: number;
    delinquent: number;
    cancelled: number;
  };
  shown: number;
  annualPremium: Money[];
  renewalWindowDays?: number;
};

/** How many policies a page brings: compact rows, «Cargar más» like «Cotizaciones recientes». */
export const POLICIES_PAGE = 20;

/**
 * The list's search, filters and chip (#236, #310): `status` is the receipts' collection status,
 * `segment` the chip. Kept while the person stays on the screen and in the URL.
 */
export type PolicyFilter = {
  q: string;
  status: string;
  carrier: string;
  productLine: string;
  agentId: string;
  endsFrom: string;
  endsTo: string;
  segment: string;
};
const NO_FILTER: PolicyFilter = {
  q: '',
  status: '',
  carrier: '',
  productLine: '',
  agentId: '',
  endsFrom: '',
  endsTo: '',
  segment: '',
};
let policyFilter: PolicyFilter = { ...NO_FILTER };
/** The policy whose page is open (its id), and the tab on screen; null: the list. */
let openPolicy: string | null = null;
let openTab: PolicyTab = 'resumen';
/**
 * The screen is painted again from its URL, by «Atrás» or by an action of its own: it keeps the
 * search, the filters and the open policy. Opened anew (from another screen), it starts clean.
 */
let restoring = false;

/** A new session (or «Pólizas y recibos» from the menu): the list, without search or filters. */
export function resetPolicies() {
  policyFilter = { ...NO_FILTER };
  openPolicy = null;
  openTab = 'resumen';
  restoring = false;
}

/** The API's parameter of each filter. */
const QUERY: [keyof PolicyFilter, string][] = [
  ['q', 'q'],
  ['status', 'collection'],
  ['carrier', 'carrier'],
  ['productLine', 'productLine'],
  ['agentId', 'agentId'],
  ['endsFrom', 'endsFrom'],
  ['endsTo', 'endsTo'],
  ['segment', 'segment'],
];

/** The query of GET /v1/policies for a search and its filters (nothing when there are none). */
export function policyQuery(f: Partial<PolicyFilter>): string {
  const q = new URLSearchParams();
  for (const [k, param] of QUERY) {
    const v = (f[k] ?? '').trim();
    if (v) q.set(param, v);
  }
  const query = q.toString();
  return query ? `?${query}` : '';
}
/** The search and filters on screen, as the API reads them (the Excel of the list). */
export const currentPolicyQuery = () => policyQuery(policyFilter);

/** The screen's own URL (#310): `?vista=polizas` with the open policy and tab, or the filters. */
const URL_KEYS: [keyof PolicyFilter, string][] = [
  ['q', 'q'],
  ['segment', 'chip'],
  ['status', 'cobranza'],
  ['carrier', 'aseguradora'],
  ['productLine', 'ramo'],
  ['agentId', 'agente'],
  ['endsFrom', 'vence-desde'],
  ['endsTo', 'vence-hasta'],
];
export function policiesUrl(): string {
  const u = new URLSearchParams({ vista: 'polizas' });
  for (const [k, name] of URL_KEYS) if (policyFilter[k]) u.set(name, policyFilter[k]);
  if (openPolicy) {
    u.set('poliza', openPolicy);
    if (openTab !== 'resumen') u.set('pestana', openTab);
  }
  return `?${u.toString()}`;
}
/** Writes the screen's URL: a new history entry (open a policy, back to the list) or this one. */
function syncUrl(push: boolean) {
  try {
    const url = `${location.pathname}${policiesUrl()}`;
    if (`${location.pathname}${location.search}` === url) return;
    if (push) history.pushState(null, '', url);
    else history.replaceState(null, '', url);
  } catch {
    // A sandboxed frame may refuse history; the screen still works without its URL.
  }
}

/**
 * Reads a URL of the screen (a reload, a link, «Atrás»): true when it is «Pólizas y recibos»,
 * with its filters and maybe a policy and a tab, now set for the next paint.
 */
export function restorePoliciesUrl(search: string): boolean {
  const u = new URLSearchParams(search);
  if (u.get('vista') !== 'polizas') return false;
  policyFilter = { ...NO_FILTER };
  for (const [k, name] of URL_KEYS) policyFilter[k] = (u.get(name) ?? '').slice(0, 120);
  openPolicy = u.get('poliza') || null;
  const tab = u.get('pestana');
  openTab = isPolicyTab(tab) ? tab : 'resumen';
  restoring = true;
  delete listPages.polizas;
  return true;
}

/**
 * Which search and filters a list answers (#263): wa-input submits on Enter a moment later, so
 * the list on screen right after Enter may still be the previous one; whoever reads it can tell.
 */
const answers = (f: PolicyFilter) =>
  `data-policy-q="${esc(f.q)}" data-policy-status="${esc(f.status)}" data-policy-carrier="${esc(f.carrier)}" data-policy-segment="${esc(f.segment)}"`;

const filtering = () =>
  Object.entries(policyFilter).some(([k, v]) => k !== 'segment' && !!v.trim());

/** Management chooses an agent; an agent sees only their own policies. */
const choosesAgent = () => !has('agent');

/** The search and filters above the list. */
function filtersBar(carriers: CarrierView[], agents: string[]) {
  const f = policyFilter;
  return `<form id="policy-search-form" class="card toolbar policy-filters" role="search" aria-label="Buscar pólizas" data-component="policy-filters">
    <div class="field grow">${textControl({ label: 'Buscar póliza, cliente o DPI', id: 'pol-q', type: 'search', maxlength: 120, autocomplete: 'off', value: f.q, placeholder: 'AUTO-0000123 · Xitumul · 0101' })}</div>
    ${field(
      'Cobranza',
      selectControl({ id: 'pol-status' }, [
        { value: '', label: 'Todos', selected: !f.status },
        ...Object.entries(POLICY_STATE).map(([value, [label]]) => ({
          value,
          label,
          selected: f.status === value,
        })),
      ]),
    )}
    ${field(
      'Aseguradora',
      selectControl({ id: 'pol-carrier' }, [
        { value: '', label: 'Todas', selected: !f.carrier },
        ...carriers.map((c) => ({
          value: c.id,
          label: c.name || carrierName(c.id),
          selected: f.carrier === c.id,
        })),
      ]),
    )}
    ${field(
      'Ramo',
      selectControl({ id: 'pol-line' }, [
        { value: '', label: 'Todos', selected: !f.productLine },
        ...Object.entries(PRODUCT).map(([value, label]) => ({
          value,
          label,
          selected: f.productLine === value,
        })),
      ]),
    )}
    ${
      choosesAgent()
        ? field(
            'Agente',
            selectControl({ id: 'pol-agent' }, [
              { value: '', label: 'Todos', selected: !f.agentId },
              ...agents.map((a) => ({ value: a, label: agentName(a), selected: f.agentId === a })),
            ]),
          )
        : ''
    }
    ${field('Vencen desde', dateControl({ id: 'pol-ends-from', value: f.endsFrom }))}
    ${field('Vencen hasta', dateControl({ id: 'pol-ends-to', value: f.endsTo }))}
    <div class="order-buttons"><button class="btn primary" type="submit" id="pol-search">Buscar</button>${filtering() ? '<button type="button" class="btn" data-policy-clear>Ver todas</button>' : ''}</div></form>`;
}

/** The chips with their counts, and the annual premium of what is shown. */
function chipsBar(s: SummaryView) {
  const window = s.renewalWindowDays ?? 60;
  const premium = s.annualPremium.length
    ? s.annualPremium.map((m) => `<b class="num">${fmt(m)}</b>`).join(' · ')
    : `<b class="num">${fmt({ amountMinor: 0, currency: 'GTQ' })}</b>`;
  return `<div class="policy-chips" data-component="policy-chips">
    <div class="chip-filters" role="group" aria-label="Pólizas por estado">${POLICY_CHIPS.map(
      ([segment, label, key]) =>
        `<button type="button" class="chip-filter" data-policy-chip="${segment}" aria-pressed="${policyFilter.segment === segment}"${segment === 'RENEWAL_DUE' ? ` title="Vencen en los próximos ${window} días"` : ''}>${esc(label)}<span class="count">${s.counts[key]}</span></button>`,
    ).join('')}</div>
    <span class="policy-total muted small" aria-live="polite" data-policy-total>${s.shown} ${s.shown === 1 ? 'póliza' : 'pólizas'} · ${premium} en prima anual</span></div>`;
}

/** A policy's row: number and ramo · plan, client and DPI, carrier, agent, premium, ends, states. */
export function policyRow(p: PolicySummary, withAgent: boolean, requested = false) {
  const [statusLabel, statusTone] = POLICY_STATUS[p.status] ?? [p.status, 'info'];
  const frequency = p.paymentFrequency
    ? `${esc(FREQUENCY[p.paymentFrequency] ?? p.paymentFrequency)}${p.installmentCount && p.installmentCount > 1 ? `<div class="policy-sub">${p.installmentCount} cuotas</div>` : ''}`
    : '<span class="muted">—</span>';
  const ends = p.period.end
    ? `${esc(date(p.period.end))}<div class="policy-sub">${esc(daysText(p.daysLeft))}</div>`
    : '<span class="muted">—</span>';
  return `<tr data-policy-row="${esc(p.id)}">
    <td data-label="Póliza"><button type="button" class="policy-link" data-policy-open="${esc(p.id)}" aria-label="Abrir la póliza ${esc(p.policyNumber)}"><span class="policy-number">${esc(p.policyNumber)}</span></button><div class="policy-sub" data-policy-line>${esc(lineAndPlan(p))}</div></td>
    <td data-label="Cliente" data-policy-client>${p.policyholder ? `<span class="policy-client">${esc(p.policyholder.fullName)}</span>${p.policyholder.nationalId ? `<div class="policy-sub mono">DPI ${esc(p.policyholder.nationalId)}</div>` : ''}` : '<span class="muted">Sin contratante registrado</span>'}</td>
    <td data-label="Aseguradora" data-policy-carrier>${carrierCell(p.carrier)}</td>
    ${withAgent ? `<td data-label="Agente">${esc(agentName(p.agentId))}</td>` : ''}
    <td data-label="Prima anual" class="num" data-policy-premium>${p.annualPremium ? fmt(p.annualPremium) : '<span class="muted">—</span>'}</td>
    <td data-label="Forma de pago" data-policy-frequency>${frequency}</td>
    <td data-label="Vence" class="nowrap" data-policy-ends>${ends}</td>
    <td data-label="Estado" data-policy-status>${pill(statusTone, statusLabel)}${requested ? `<div>${pill('warn', 'Cancelación solicitada')}</div>` : ''}</td>
    <td data-label="Cobranza" data-policy-state>${p.collection ? collectionPill(p.collection) : '<span class="muted">—</span>'}</td></tr>`;
}

/** The open policy's page (or why it cannot be opened). */
async function policyView(id: string) {
  stopWatching();
  try {
    return policyPageHtml(await loadPolicyPage(id), openTab);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403))
      return policyMissing(
        e.status === 404
          ? 'La agencia no tiene una póliza con ese número de vigencia.'
          : 'Esta póliza es de otro agente: cada agente ve las suyas.',
      );
    throw e;
  }
}

export async function polizas() {
  // A screen opened anew (from another screen) starts without search, filters or open policy.
  const fresh = !restoring && !document.querySelector('#policy-search-form, #policy-page');
  restoring = false;
  if (fresh) resetPolicies();
  syncUrl(false);
  if (openPolicy) return policyView(openPolicy);
  // The search this list answers: another one typed while it loads paints its own list.
  const asked = policyFilter;
  const query = policyQuery(asked);
  const [policyPages, summary, operations, carriers, agents, requests] = await Promise.all([
    paged<PolicySummary>('polizas', `/v1/policies${query}`, POLICIES_PAGE),
    api<SummaryView>('GET', `/v1/policies/summary${query}`),
    api<OperationView[]>('GET', '/v1/carrier-operations'),
    api<CarrierView[]>('GET', '/v1/carriers'),
    choosesAgent()
      ? api<{ agentId: string }[]>('GET', '/v1/agents')
          .then((a) => a.map((x) => x.agentId))
          .catch(() => [] as string[])
      : Promise.resolve([] as string[]),
    // The cancellations agents asked for and the direction has not decided (#311).
    api<CancellationRequestView[]>('GET', '/v1/cancellation-requests').catch(
      () => [] as CancellationRequestView[],
    ),
  ]);
  watchReviews(operations);
  const reviews = reviewsSection(operations);
  const policies = policyPages.items;
  const head = pageHead(
    'Pólizas y recibos',
    has('agency_director', 'agency_admin', 'cashier', 'agent')
      ? 'Cada póliza con su vigencia, su prima y sus recibos. Búscala y ábrela para ver todo lo que tiene.'
      : 'Cada póliza de los agentes de la agencia con su vigencia, su prima y su cobranza.',
  );
  if (!policies.length && !filtering() && !asked.segment)
    return (
      head +
      reviews +
      renewalsLink() +
      empty(
        'policy',
        'Todavía no hay pólizas',
        'Emite la primera desde <strong>Cotizar</strong>; su plan de recibos aparecerá aquí.',
        canQuote()
          ? `<button type="button" class="btn primary" data-go="cotizar">${icon('bolt')}Ir a cotizar</button>`
          : '',
      )
    );
  const filters = filtersBar(carriers, agents);
  const chips = chipsBar(summary);
  const exportButton = `<button type="button" class="btn sm" data-report="policies" data-format="xlsx">${icon('download')}Exportar a Excel</button>`;
  if (!policies.length)
    return `${head}${reviews}${renewalsLink()}${filters}${chips}${empty(
      'policy',
      'Ninguna póliza coincide',
      policyFilter.q.trim()
        ? `No hay pólizas con «${esc(policyFilter.q.trim())}»${Object.entries(asked).some(([k, v]) => k !== 'q' && !!v.trim()) ? ' con esos filtros' : ''}. Prueba con el número completo, el apellido del cliente o los últimos dígitos de su DPI.`
        : 'No hay pólizas con esos filtros. Prueba con otro estado, otra aseguradora u otras fechas.',
      '<button type="button" class="btn" data-policy-clear>Ver todas</button>',
    )}${collectionsReport()}`;
  const withAgent = choosesAgent();
  // A cancellation an agent asked for and the direction has not decided (#311).
  const requested = new Set(
    (Array.isArray(requests) ? requests : [])
      .filter((r) => r.status === 'PENDING')
      .map((r) => r.policyId),
  );
  const count = `${policies.length} ${policies.length === 1 ? 'póliza' : 'pólizas'}${policyPages.more ? ' por ahora' : ''}`;
  return `${head}${reviews}${renewalsLink()}${filters}${chips}
    <section class="policies" aria-label="Pólizas" ${answers(asked)}><div class="section-head policies-head"><h2>${filtering() ? 'Resultados' : 'Pólizas'} <span class="muted small" aria-live="polite">${count}</span></h2>${exportButton}</div>
    <div class="card policies-card"><div class="table-wrap" data-component="table"><table class="table policies-table"><thead><tr><th>Póliza</th><th>Cliente</th><th>Aseguradora</th>${withAgent ? '<th>Agente</th>' : ''}<th class="num">Prima anual</th><th>Forma de pago</th><th>Vence</th><th>Estado</th><th>Cobranza</th></tr></thead>
    <tbody>${policies.map((p) => policyRow(p, withAgent, requested.has(p.id))).join('')}</tbody></table></div></div></section>${loadMore('polizas', policyPages.more, policies.length, 'pólizas')}${collectionsReport()}`;
}

/** The tab the person moved to with the keyboard: kept in the URL (bindPolicyTabs). */
export function selectPolicyTab(tab: string) {
  if (!isPolicyTab(tab)) return;
  openTab = tab;
  syncUrl(false);
}

/** Opens a policy's page (a new entry in the history: «Atrás» goes back to the list). */
async function openPolicyPage(id: string, tab: PolicyTab = 'resumen') {
  openPolicy = id;
  openTab = tab;
  restoring = true;
  syncUrl(true);
  await render('polizas', { quiet: true });
  window.scrollTo({ top: 0 });
  $('#policy-page')?.focus({ preventScroll: true });
}

/** Back to the list, with the search and filters it had; the focus on the policy's row. */
async function backToList() {
  const was = openPolicy;
  openPolicy = null;
  openTab = 'resumen';
  restoring = true;
  syncUrl(true);
  await render('polizas', { quiet: true });
  const row = was
    ? document.querySelector<HTMLElement>(`[data-policy-open="${CSS.escape(was)}"]`)
    : null;
  (row ?? $('#pol-q'))?.focus();
}

/**
 * «Más acciones» menus close when the person picks an action, clicks elsewhere or presses
 * Escape (the focus goes back to «Más acciones»).
 */
export function bindMoreMenus(root: Document) {
  root.addEventListener(
    'click',
    (ev) => {
      const target = ev.target as HTMLElement;
      const inside = target.closest<HTMLDetailsElement>('details[data-more-actions]');
      const picked = !!target.closest('details[data-more-actions] .menu button');
      for (const d of root.querySelectorAll<HTMLDetailsElement>('details[data-more-actions][open]'))
        if (d !== inside || picked) d.open = false;
    },
    true,
  );
  root.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key !== 'Escape') return;
    const open = (ev.target as HTMLElement).closest?.<HTMLDetailsElement>(
      'details[data-more-actions][open]',
    );
    if (!open) return;
    open.open = false;
    open.querySelector<HTMLElement>('summary')?.focus();
  });
}

// ------------------------------------------------------------------ en revisión de la aseguradora

/** An issuance or endorsement the carrier has under review (GET /v1/carrier-operations). */
type OperationView = {
  carrier: string;
  kind: 'ISSUE' | 'ENDORSE';
  carrierRef: string;
  agentId: string;
  productLine: string;
  option: string | null;
  holderName: string | null;
  state: 'PENDING' | 'DONE' | 'REJECTED';
  policyNumber: string | null;
  scheduleId: string | null;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
};

const opKey = (o: OperationView) => `${o.carrier}:${o.kind}:${o.carrierRef}`;
const opState = (o: OperationView) =>
  o.state === 'PENDING'
    ? pill('warn', 'En revisión')
    : o.state === 'DONE'
      ? pill('ok', o.kind === 'ISSUE' ? 'Emitida' : 'Aplicado')
      : pill('bad', 'Rechazada');

/**
 * «En revisión de la aseguradora»: what the agency asked and the carrier is still reviewing
 * (G&T's solicitudes and endorsements), and what it answered lately. It updates itself: the
 * carrier's notice (or Nubi asking) changes it without reloading the page.
 */
function reviewsSection(ops: OperationView[]) {
  // Under review, and what was answered in the last week.
  const week = Date.now() - 7 * 86_400_000;
  const shown = ops.filter((o) => o.state === 'PENDING' || Date.parse(o.updatedAt) >= week);
  if (!shown.length) return '<div id="carrier-reviews"></div>';
  const canFollow = has('agency_director', 'agency_admin', 'agent');
  const rows = shown
    .map(
      (o) => `<tr data-review="${esc(opKey(o))}" data-state="${esc(o.state)}">
      <td><span class="mono">${esc(o.carrierRef)}</span><div class="muted small">${o.kind === 'ISSUE' ? 'Solicitud de emisión' : 'Endoso'}</div></td>
      <td>${carrierCell(o.carrier)}</td>
      <td>${esc(o.option ?? o.productLine)}${o.holderName ? `<div class="muted small">${esc(o.holderName)}</div>` : ''}</td>
      <td class="nowrap">${esc(when(o.createdAt))}</td>
      <td>${opState(o)}${o.state === 'DONE' && o.policyNumber ? `<div class="small">Póliza <span class="mono">${esc(o.policyNumber)}</span></div>` : ''}${o.state === 'REJECTED' && o.reason ? `<div class="muted small">${esc(o.reason)}</div>` : ''}</td>
      <td class="actions">${o.state === 'PENDING' && canFollow ? `<button type="button" class="btn sm" data-follow-op="${esc(o.carrier)}" data-kind="${esc(o.kind)}" data-ref="${esc(o.carrierRef)}">Consultar ahora</button>` : ''}</td></tr>`,
    )
    .join('');
  const pending = shown.filter((o) => o.state === 'PENDING').length;
  return `<section class="card" id="carrier-reviews" aria-label="En revisión de la aseguradora" data-component="carrier-reviews">
    <div class="card-head"><h2>En revisión de la aseguradora</h2><span class="spacer"></span>
    <span class="muted small">${pending ? `${pending} en revisión · se actualiza sola cuando la aseguradora avisa` : 'La aseguradora ya respondió'}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Referencia</th><th>Aseguradora</th><th>Plan</th><th>Pedida</th><th>Estado</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>${rows}</tbody></table></div></section>`;
}

/** Re-reads the operations under review every few seconds while some are, on this screen only. */
let watcher: ReturnType<typeof setInterval> | null = null;
/** A policy's page does not follow the list's reviews. */
function stopWatching() {
  if (watcher) clearInterval(watcher);
  watcher = null;
}
function watchReviews(initial: OperationView[]) {
  stopWatching();
  if (!initial.some((o) => o.state === 'PENDING')) return;
  const before = new Map(initial.map((o) => [opKey(o), o.state]));
  const timer = setInterval(async () => {
    if (current !== 'polizas' || !document.querySelector('#carrier-reviews')) {
      clearInterval(timer);
      if (watcher === timer) watcher = null;
      return;
    }
    const now = await api<OperationView[]>('GET', '/v1/carrier-operations').catch(() => null);
    if (!now || watcher !== timer) return;
    const answered = now.filter((o) => before.get(opKey(o)) === 'PENDING' && o.state !== 'PENDING');
    if (!answered.length) return;
    clearInterval(timer);
    watcher = null;
    for (const o of answered)
      toast(
        o.state === 'DONE'
          ? o.kind === 'ISSUE'
            ? `${carrierName(o.carrier)} aceptó la solicitud ${o.carrierRef}: póliza ${o.policyNumber ?? ''} emitida.`
            : `${carrierName(o.carrier)} aplicó el endoso ${o.carrierRef}.`
          : `${carrierName(o.carrier)} rechazó ${o.kind === 'ISSUE' ? 'la solicitud' : 'el endoso'} ${o.carrierRef}.`,
        o.state === 'DONE' ? 'ok' : 'error',
      );
    // The new policy (or its new receipts) appear without leaving the screen.
    await render(current, { quiet: true });
  }, 4000);
  watcher = timer;
}

// ------------------------------------------------------------------ renovaciones y consentimiento

/** «Reporte de cobros»: the receipts that fall due in a range of months, as Excel or PDF. */
function collectionsReport() {
  const month = guatemalaMonth(new Date());
  const [y, m] = month.split('-').map(Number) as [number, number];
  const t = y * 12 + (m - 1) - 5;
  const from = `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
  return `<section class="card toolbar" aria-label="Reporte de cobros">
    <div class="grow"><strong>Reporte de cobros</strong><span class="muted small">Los recibos que vencen en esos meses, con lo cobrado y lo pendiente por moneda.</span></div>
    ${field('Desde', monthControl({ id: 'rep-from', value: from }))}
    ${field('Hasta', monthControl({ id: 'rep-to', value: month }))}
    ${reportButtons('collections', 'los cobros')}</section>`;
}

/**
 * The policies coming to their end are worked in «Renovaciones» (#308): a link to it, for whoever
 * follows renewals.
 */
function renewalsLink() {
  if (!followsRenewals()) return '';
  return `<section class="card toolbar renewals-link" aria-label="Renovaciones">
    <div class="grow"><strong>Renovaciones</strong><span class="muted small">Las pólizas que vencen, su gestión y la tasa de renovación.</span></div>
    <button type="button" class="btn" data-go="renovaciones">${icon('trend')}Ver renovaciones</button></section>`;
}

/**
 * Takes the person to a policy (#310: its own page): after issuing it, from a notice or from the
 * customer's page. With `renew`, its renewal offer opens when the policy can be renewed here.
 */
export async function focusPolicy(scheduleId: string, { renew = false } = {}) {
  await openPolicyPage(scheduleId);
  const page = document.querySelector<HTMLElement>(
    `#policy-page[data-policy="${CSS.escape(scheduleId)}"]`,
  );
  if (!page) {
    toast('Esa póliza ya no aparece en tu lista.', 'error');
    return;
  }
  page.classList.remove('policy-focus');
  void page.offsetWidth;
  page.classList.add('policy-focus');
  setTimeout(() => page.classList.remove('policy-focus'), 2600);
  if (!renew) return;
  const open = page.querySelector<HTMLButtonElement>(
    `[data-renew-open="${CSS.escape(scheduleId)}"]`,
  );
  if (open && open.getAttribute('aria-expanded') !== 'true') open.click();
}

/** Searches again with what the filter bar says now, from the first page. */
function searchPolicies(focus: string) {
  const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value ?? '';
  policyFilter = {
    ...policyFilter,
    q: value('pol-q').trim(),
    status: value('pol-status'),
    carrier: value('pol-carrier'),
    productLine: value('pol-line'),
    agentId: choosesAgent() ? value('pol-agent') : '',
    endsFrom: value('pol-ends-from'),
    endsTo: value('pol-ends-to'),
  };
  delete listPages.polizas;
  restoring = true;
  void render('polizas', { quiet: true }).then(() => $(`#${focus}`)?.focus());
}

/** What this view answers: its buttons (by data attribute), forms (by id) and controls (by id). */
export const actions: Actions = {
  submit: {
    'policy-search-form': (ev) => {
      ev.preventDefault();
      searchPolicies('pol-q');
    },
  },
  change: {
    'pol-status': () => searchPolicies('pol-status'),
    'pol-carrier': () => searchPolicies('pol-carrier'),
    'pol-line': () => searchPolicies('pol-line'),
    'pol-agent': () => searchPolicies('pol-agent'),
    'pol-ends-from': () => searchPolicies('pol-ends-from'),
    'pol-ends-to': () => searchPolicies('pol-ends-to'),
  },
  click: {
    // Vouchers, reversals and collection efforts on the policy page (#315).
    ...cobranzaClicks,
    policyOpen: (_, d) => {
      // A tab may come with it (the arrears list opens «Prima y recibos», #315).
      if (d.policyOpen) void openPolicyPage(d.policyOpen, isPolicyTab(d.tab) ? d.tab : 'resumen');
    },
    policyBack: () => void backToList(),
    policyTab: (_, d) => {
      if (!isPolicyTab(d.policyTab)) return;
      openTab = d.policyTab;
      selectTab(document, d.policyTab, true);
      syncUrl(false);
    },
    // «Historial de endosos y cancelación» in «Más acciones»: the page's tab (#311).
    policyGotoTab: (_, d) => {
      if (!isPolicyTab(d.policyGotoTab)) return;
      openTab = d.policyGotoTab;
      selectTab(document, d.policyGotoTab, true);
      syncUrl(false);
    },
    policyChip: (_, d) => {
      policyFilter = { ...policyFilter, segment: d.policyChip ?? '' };
      delete listPages.polizas;
      restoring = true;
      const chip = d.policyChip ?? '';
      void render('polizas', { quiet: true }).then(() =>
        $(`[data-policy-chip="${CSS.escape(chip)}"]`)?.focus(),
      );
    },
    policyClear: () => {
      policyFilter = { ...NO_FILTER };
      delete listPages.polizas;
      restoring = true;
      void render('polizas', { quiet: true }).then(() => $('#pol-q')?.focus());
    },
    policyDoc: (t, d) => {
      if (!d.policyDoc) return;
      const label = DOCUMENT[d.kind ?? ''] ?? 'Documento';
      void act(t, `Pidiendo ${label.toLowerCase()} a la aseguradora…`, async () => {
        const name = await download(
          `/v1/schedules/${encodeURIComponent(d.policyDoc ?? '')}/documents/${encodeURIComponent(d.kind ?? '')}`,
          `${label.toLowerCase()}-${d.number ?? ''}.pdf`,
        );
        return d.kind === 'POLICY'
          ? `Póliza ${d.number ?? ''} descargada en PDF (${name}).`
          : `${label} de la póliza ${d.number ?? ''} ${d.kind === 'CERTIFICATE' ? 'descargado' : 'descargada'} en PDF (${name}).`;
      });
    },
    followOp: (t, d) => {
      if (!d.followOp) return;
      void act(t, 'Consultando a la aseguradora…', async () => {
        const o = await api<OperationView>(
          'POST',
          `/v1/carrier-operations/${encodeURIComponent(d.followOp ?? '')}/${encodeURIComponent(d.kind ?? '')}/${encodeURIComponent(d.ref ?? '')}/follow`,
        );
        await render(current, { quiet: true });
        return o.state === 'PENDING'
          ? `La aseguradora todavía revisa ${o.carrierRef}.`
          : o.state === 'DONE'
            ? o.kind === 'ISSUE'
              ? `Póliza ${o.policyNumber ?? ''} emitida.`
              : `Endoso ${o.carrierRef} aplicado.`
            : `La aseguradora rechazó ${o.carrierRef}.`;
      });
    },
    payLink: (t, d) => {
      if (!d.payLink) return;
      const amount = JSON.parse(d.amount ?? '{}') as Money;
      void act(t, 'Enviando el enlace de pago…', async () => {
        const r = await api<{
          expiresAt: string;
          delivery: { channel: string; status: string; simulated: boolean } | null;
        }>('POST', `/v1/schedules/${encodeURIComponent(d.payLink ?? '')}/payment-links`, {
          installment: Number(d.n),
        });
        const by = r.delivery?.channel === 'EMAIL' ? 'correo' : 'WhatsApp';
        if (r.delivery?.status === 'FAILED')
          throw new InputError(`No se pudo enviar el enlace por ${by}. Intenta de nuevo.`);
        const until = new Intl.DateTimeFormat('es-GT', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'America/Guatemala',
        }).format(new Date(r.expiresAt));
        return `Enlace de pago de ${fmt(amount)} enviado por ${by}${r.delivery?.simulated ? ' (simulado)' : ''}; vence el ${until}.`;
      });
    },
    pay: (t, d) => {
      if (!d.pay) return;
      const amount = JSON.parse(d.amount ?? '{}') as Money;
      const cash = d.method === 'CASH';
      void act(t, 'Registrando el cobro…', async () => {
        await api('POST', `/v1/schedules/${encodeURIComponent(d.pay ?? '')}/payments`, {
          installment: Number(d.n),
          amount,
          method: cash ? 'CASH' : 'TRANSFER',
          reference: `WEB-${Date.now()}`,
          ...(cash ? { cashBoxId: d.box } : {}),
        });
        await render(current, { quiet: true });
        return cash
          ? `Cobro en efectivo de ${fmt(amount)} registrado en ${d.box}.`
          : `Cobro de ${fmt(amount)} registrado.`;
      });
    },
    renewOpen: (t, d) => {
      if (!d.renewOpen) return;
      const panel = document.querySelector<HTMLElement>(
        `[data-renew-panel="${CSS.escape(d.renewOpen)}"]`,
      );
      if (!panel) return;
      panel.hidden = !panel.hidden;
      t.setAttribute('aria-expanded', String(!panel.hidden));
      if (panel.hidden) return;
      panel.innerHTML = `<div class="toolbar">${skel(14, '40%')}</div>`;
      const id = d.renewOpen;
      void act(t, 'Pidiendo la oferta de renovación a la aseguradora…', async () => {
        const o = await api<{
          keepsPolicyNumber: boolean;
          startsOn: string;
          quote: { decision: string; validUntil: string; options: Option[] };
        }>('GET', `/v1/schedules/${encodeURIComponent(id)}/renewal-offer`);
        const priced = o.quote.options.filter((x) => x.premium);
        panel.innerHTML = `<div class="toolbar"><div class="grow"><strong>Oferta de renovación</strong>
        <span class="muted small">Nueva vigencia desde el ${esc(date(o.startsOn))} · ${o.keepsPolicyNumber ? 'conserva el número de póliza' : 'la aseguradora emite un número nuevo'} · válida hasta el ${esc(date(o.quote.validUntil))}</span></div>${pill(o.quote.decision)}</div>
        ${
          priced.length
            ? `<div class="table-wrap" data-component="table"><table class="table quote"><thead><tr><th>Plan</th><th class="num">Prima anual</th><th class="num">Cuotas</th><th class="num">Tu comisión</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
          ${priced
            .map(
              (
                x,
              ) => `<tr><td><span class="option-name">${esc(x.carrierOptionCode)}</span></td><td class="num"><span class="premium">${fmt(x.premium?.gross)}</span></td>
              <td class="num">${x.premium?.installments.count} × ${fmt(x.premium?.installments.regular)}</td><td class="num">${fmt(x.commission)}</td>
              <td class="actions"><button type="button" class="btn sm primary" data-renew-send="${esc(id)}" data-option="${esc(x.carrierOptionCode)}" aria-label="Renovar con ${esc(x.carrierOptionCode)}">Renovar</button></td></tr>`,
            )
            .join('')}</tbody></table></div>`
            : '<p class="note">La aseguradora no ofrece opciones con precio para renovar esta póliza.</p>'
        }`;
        return `Oferta recibida: ${priced.length} opciones.`;
      });
    },
    renewSend: (t, d) => {
      if (!d.renewSend) return;
      void act(t, 'Renovando con la aseguradora…', async () => {
        const r = await api<{
          policyNumber: string;
          period: { start: string };
          keepsPolicyNumber: boolean;
        }>('POST', `/v1/schedules/${encodeURIComponent(d.renewSend ?? '')}/renew`, {
          option: d.option,
        });
        await render(current, { quiet: true });
        return `Póliza ${r.policyNumber} renovada desde el ${date(r.period.start)}${r.keepsPolicyNumber ? '' : ' (número nuevo)'}.`;
      });
    },
    endorseOpen: (t, d) => {
      if (!d.endorseOpen) return;
      const panel = document.querySelector<HTMLElement>(
        `[data-endorse-panel="${CSS.escape(d.endorseOpen)}"]`,
      );
      if (panel) {
        panel.hidden = !panel.hidden;
        t.setAttribute('aria-expanded', String(!panel.hidden));
      }
    },
  },
};
